
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { usePackageCart } from "@/hooks/usePackageCart";
import { useUI } from "@/contexts/UIContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { BranchSelector } from "@/components/admin/BranchSelector";
import { ShoppingCart, User, LogOut, ShieldCheck, Heart, Settings, Users, Truck, ChefHat, DollarSign, Headphones, MapPin, Navigation, Menu, Moon, Sun, Monitor, Smartphone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const AdminHeader = () => {
  const { user, isAdmin, signOut } = useAuth();
  const { currentBranch, accessibleBranches } = useBranch();
  const { toggleSidebar, isDesktopView, toggleDesktopView } = useUI();
  const { setTheme, actualTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { items: cartItems } = usePackageCart(user?.id);

  // Calculate cart item count for both products and packages
  const cartItemCount = cartItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Fetch user roles
  const { data: userRoles } = useQuery({
    queryKey: ["user_roles", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      
      if (error) throw error;
      return data.map(r => r.role);
    },
    enabled: !!user?.id,
  });

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error: unknown) {
      toast.error("Error signing out");
    }
  };

  // Check if user has specific roles
  const hasRole = (role: string) => userRoles?.includes(role) || false;
  const isKitchen = hasRole('kitchen');
  const isDispatcher = hasRole('dispatcher');
  const isRider = hasRole('rider');
  const isCustomerService = hasRole('customer_service');

  // Check if user is ONLY a specific role (no admin privileges)
  const isOnlyRider = isRider && !isAdmin;
  const isOnlyKitchen = isKitchen && !hasRole('admin') && !hasRole('super_admin');
  const isOnlyCustomerService = isCustomerService && !hasRole('admin') && !hasRole('super_admin');
  const isOnlyDispatcher = isDispatcher && !hasRole('admin') && !hasRole('super_admin');

  // Show cart only on customer-facing routes
  const showCart = !location.pathname.startsWith("/admin");
  
  // Show branch selector for admin users
  const showBranchSelector = isAdmin && accessibleBranches.length > 0;
  
  // Show admin panel button for admins on customer side
  const showAdminButton = isAdmin && !location.pathname.startsWith("/admin");

  // Get user display name
  const displayName = profile?.first_name || user?.user_metadata?.first_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.charAt(0).toUpperCase();

  // Determine user badge
  const getUserBadge = () => {
    if (isAdmin) return { text: 'Admin', variant: 'destructive' as const, icon: ShieldCheck };
    if (isKitchen) return { text: 'Kitchen', variant: 'secondary' as const, icon: ChefHat };
    if (isDispatcher) return { text: 'Dispatcher', variant: 'outline' as const, icon: MapPin };
    if (isRider) return { text: 'Rider', variant: 'outline' as const, icon: Navigation };
    if (isCustomerService) return { text: 'Support', variant: 'secondary' as const, icon: Headphones };
    return { text: 'Customer', variant: 'default' as const, icon: User };
  };

  const userBadge = getUserBadge();

  return (
    <header className="bg-white border-b shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          {/* Sidebar Toggle for Admin */}
          {isAdmin && (
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <button 
            onClick={() => navigate('/')}
            className="text-lg sm:text-xl font-bold text-primary hover:text-primary/80 transition-colors truncate max-w-[100px] xs:max-w-[140px] sm:max-w-none"
          >
            Dough Man
          </button>
        </div>

        {/* Right side navigation */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Branch Selector (for admin users) */}
          {showBranchSelector && (
            <BranchSelector variant="header" className="flex" />
          )}
          
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(actualTheme === 'dark' ? 'light' : 'dark')}
            className="hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {actualTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          
          {/* Admin Panel Button (for admins on customer side) */}
          {showAdminButton && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate("/admin/dashboard")}
              className="flex items-center gap-2"
            >
              <ShieldCheck className="h-4 w-4" />
              Admin Panel
            </Button>
          )}
          
          {/* Cart (for customers only) */}
          {showCart && (
            <Button variant="ghost" size="icon" onClick={() => navigate("/cart")} className="relative" data-testid="cart-button">
              <ShoppingCart className="h-5 w-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full px-1 text-xs" data-testid="cart-counter">
                  {cartItemCount}
                </span>
              )}
            </Button>
          )}

          {/* User Authentication */}
          {user ? (
            <div className="flex items-center gap-3">
              {/* User Badge */}
              <Badge variant={userBadge.variant} className="flex items-center gap-1 px-1 sm:px-2 py-0.5">
                <userBadge.icon className="h-3 w-3" />
                <span className="hidden min-[400px]:inline">{userBadge.text}</span>
              </Badge>

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || ""} alt={displayName} />
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  
                  {/* Customer Menu Items */}
                  {!isAdmin && !isKitchen && !isDispatcher && !isRider && !isCustomerService && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/account")}>
                        <User className="mr-2 h-4 w-4" />
                        Account
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/wishlist")}>
                        <Heart className="mr-2 h-4 w-4" />
                        Wishlist
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/orders")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        My Orders
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Rider-only Menu Items */}
                  {isOnlyRider && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/rider-dashboard")}>
                        <Navigation className="mr-2 h-4 w-4" />
                        My Dashboard
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Kitchen-only Menu Items */}
                  {isOnlyKitchen && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/kitchen")}>
                        <ChefHat className="mr-2 h-4 w-4" />
                        Kitchen
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Orders
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Customer Service-only Menu Items */}
                  {isOnlyCustomerService && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/customer-service")}>
                        <Headphones className="mr-2 h-4 w-4" />
                        Customer Service
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Orders
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Dispatcher-only Menu Items */}
                  {isOnlyDispatcher && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/dispatcher")}>
                        <Truck className="mr-2 h-4 w-4" />
                        Dispatch Center
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/riders")}>
                        <Navigation className="mr-2 h-4 w-4" />
                        Riders
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Orders
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Admin/Super Admin Menu Items (full access) */}
                  {(hasRole('admin') || hasRole('super_admin')) && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/dashboard")}>
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Orders
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/kitchen")}>
                        <ChefHat className="mr-2 h-4 w-4" />
                        Kitchen
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/dispatcher")}>
                        <Truck className="mr-2 h-4 w-4" />
                        Dispatch
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/customer-service")}>
                        <Headphones className="mr-2 h-4 w-4" />
                        Customer Service
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/riders")}>
                        <Navigation className="mr-2 h-4 w-4" />
                        Riders
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/user-management")}>
                        <Users className="mr-2 h-4 w-4" />
                        User Management
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/analytics")}>
                        <ChefHat className="mr-2 h-4 w-4" />
                        Analytics
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/cash-tracking")}>
                        <DollarSign className="mr-2 h-4 w-4" />
                        Cash Tracking
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator />
                  
                  {/* Customer Screen */}
                  <DropdownMenuItem onClick={() => navigate("/")}>
                    <Users className="mr-2 h-4 w-4" />
                    Customer Section
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  
                  {/* Settings (for all authenticated users) */}
                  <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={toggleDesktopView}>
                    {isDesktopView ? (
                      <Smartphone className="mr-2 h-4 w-4" />
                    ) : (
                      <Monitor className="mr-2 h-4 w-4" />
                    )}
                    {isDesktopView ? "Mobile View" : "Desktop View"}
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  {/* Sign Out */}
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button onClick={() => navigate("/auth")} variant="default">
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
